import * as R from 'remeda';
import {
  BsonDocument,
  ProcessingTextMode,
  WebBotBackgroundImageMode,
  WebBotBackgroundTarget,
  WebBotBackgroundType,
  WebBotHeaderFooterStyle,
  WebBotProviderData,
  WidgetButtonShape,
  WebBotCustomUiMode,
} from '@/API';
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Modal,
  Stack,
  Tabs,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { PropsWithChildren, useEffect, useState } from 'react';
import { IconEye, IconEyeOff } from '@tabler/icons-react';
import { AppSplitter } from '@/Components/AppSplitter';
import { WebBotExtensionEdit } from './Extension/WebBotExtensionEdit';
import { WebBotLivePreview } from './WebBotLivePreview';
import { WebBotFeaturesTab } from './Tabs/WebBotFeaturesTab';
import { WebBotLayoutTab } from './Tabs/WebBotLayoutTab';
import { WebBotResponsiveTab } from './Tabs/WebBotResponsiveTab';
import { WebBotSecurityTab } from './Tabs/WebBotSecurityTab';
import { WebBotStyleTab } from './Tabs/WebBotStyleTab';
import { WebBotTextTab } from './Tabs/WebBotTextTab';

export const defaultWebBotProviderData: WebBotProviderData = {
  security: {
    enableSecurity: false,
    secretKey: '',
    allowedDomains: [],
  },
  text: {
    title: 'ChatBot',
    botName: '',
    inputPlaceholder: '輸入訊息',
    footerText: '',
    feedbackNotificationText: '',
    defaultQuestions: [],
    processingTexts: [],
    processingTextMode: ProcessingTextMode.Sequence,
  },
  theme: {
    themeColor: '#007bff',
  },
  background: {
    color: '',
    type: WebBotBackgroundType.Color,
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    target: WebBotBackgroundTarget.Window,
    imageMode: WebBotBackgroundImageMode.Cover,
    imageScale: 100,
    imageOffsetX: 0,
    imageOffsetY: 0,
    opacity: 100,
  },
  header: {
    style: WebBotHeaderFooterStyle.Default,
    backgroundColor: '',
    textColor: '',
    blur: 8,
    opacity: 60,
  },
  footer: {
    style: WebBotHeaderFooterStyle.Default,
    backgroundColor: '',
    textColor: '',
    blur: 8,
    opacity: 60,
  },
  userBubble: {
    color: '',
    textColor: '',
    opacity: 100,
    blur: 0,
    borderWidth: 0,
    borderColor: '',
    borderRadius: 15,
    enableDirectionRadius: true,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    shadowBlur: 0,
    shadowColor: '',
  },
  botBubble: {
    color: '',
    textColor: '',
    opacity: 100,
    blur: 0,
    borderWidth: 0,
    borderColor: '',
    borderRadius: 15,
    enableDirectionRadius: true,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    shadowBlur: 0,
    shadowColor: '',
  },
  messageMeta: {
    botNameColor: '',
    metaColor: '',
    enableBadge: false,
    badgeBackgroundColor: '',
    badgeOpacity: 65,
    badgeBlur: 4,
  },
  layout: {
    positionRight: undefined,
    positionBottom: undefined,
    positionLeft: undefined,
    positionTop: undefined,
    windowWidth: 350,
    windowHeight: 500,
    windowOffsetX: undefined,
    windowOffsetY: undefined,
    windowShadow: 20,
    windowShadowOffsetX: 0,
    windowShadowOffsetY: 3,
    windowShadowBlur: 8,
    windowShadowColor: '',
    buttonShape: WidgetButtonShape.Circle,
    buttonWidth: 60,
    buttonHeight: 60,
    buttonShadow: 20,
    buttonShadowOffsetX: 0,
    buttonShadowOffsetY: 3,
    buttonShadowBlur: 8,
    buttonShadowColor: '',
  },
  responsive: {
    breakpoint: 768,
    forceFullScreen: false,
    windowWidth: undefined,
    windowHeight: undefined,
    positionRight: undefined,
    positionBottom: undefined,
    positionLeft: undefined,
    positionTop: undefined,
    windowOffsetX: undefined,
    windowOffsetY: undefined,
    buttonWidth: undefined,
    buttonHeight: undefined,
  },
  features: {
    enableClearHistoryButton: false,
    enableStreamMode: false,
    enableFeedbackRatingButtons: false,
    enableReportIssueButton: false,
  },
  extensions: [],
  customUi: {
    enabled: false,
    entryFile: 'index.html',
    mode: WebBotCustomUiMode.Window,
  },
};

function cleanMerge<T extends Record<string, any>>(target?: T, source?: Record<string, any>): T {
  const result: any = { ...(target ?? {}) };
  if (!source) return result;
  for (const key of Object.keys(source)) {
    const val = source[key];
    if (val !== null && val !== undefined) {
      result[key] = val;
    }
  }
  return result;
}

function deepMergeWebBotData(target: WebBotProviderData, source?: Record<string, any>): WebBotProviderData {
  if (!source) return { ...target };
  return {
    security: cleanMerge(target.security, source.security),
    text: cleanMerge(target.text, source.text),
    theme: cleanMerge(target.theme, source.theme),
    background: cleanMerge(target.background, source.background),
    header: cleanMerge(target.header, source.header),
    footer: cleanMerge(target.footer, source.footer),
    userBubble: cleanMerge(target.userBubble, source.userBubble),
    botBubble: cleanMerge(target.botBubble, source.botBubble),
    messageMeta: cleanMerge(target.messageMeta, source.messageMeta),
    layout: cleanMerge(target.layout, source.layout),
    responsive: cleanMerge(target.responsive, source.responsive),
    features: cleanMerge(target.features, source.features),
    extensions: Array.isArray(source.extensions) ? source.extensions : (target.extensions ?? []),
    customUi: cleanMerge(target.customUi, source.customUi),
  };
}

export function WebBotProviderDataEditForm(props: {
  value: BsonDocument;
  onChange: (value: BsonDocument) => void;
  workspaceId?: string;
  botProviderId?: string;
  activeTab?: string | null;
  onTabChange?: (tab: string | null) => void;
  onSubmit?: (value: BsonDocument) => void;
  formId?: string;
  enableLivePreview?: boolean;
}) {
  const [internalTab, setInternalTab] = useState<string | null>('text');
  const [showPreview, setShowPreview] = useState(props.enableLivePreview ?? true);
  const activeTab = props.activeTab !== undefined ? props.activeTab : internalTab;

  const handleTabChange = (tab: string | null) => {
    if (props.activeTab === undefined) {
      setInternalTab(tab);
    }
    props.onTabChange?.(tab);
  };

  const initialData = (props.value ?? {}) as unknown as Record<string, any>;
  const form = useForm<WebBotProviderData>({
    initialValues: deepMergeWebBotData(defaultWebBotProviderData, initialData),
    validate: {
      text: {
        title: (value) => (!value ? '標題不得為空' : null),
      },
      theme: {
        themeColor: (value) => (!value ? 'ThemeColor 不得為空' : null),
      },
      security: {
        secretKey: (value, values) =>
          values.security?.enableSecurity && !value ? '啟用安全驗證時，SecretKey 不得為空' : null,
      },
    },
    onValuesChange: (values) => props.onChange(values as unknown as BsonDocument),
  });

  useEffect(() => {
    const mergedValues = deepMergeWebBotData(
      defaultWebBotProviderData,
      props.value as unknown as Record<string, any>
    );
    if (!R.isDeepEqual(form.values, mergedValues)) {
      form.setValues(mergedValues);
    }
  }, [props.value]);

  const handleFormSubmit = form.onSubmit(
    (values) => {
      props.onSubmit?.(values as unknown as BsonDocument);
    },
    (errors) => {
      if (errors['text.title'] || errors['text.botName'] || errors['text.inputPlaceholder']) {
        handleTabChange('text');
      } else if (errors['theme.themeColor'] || errors['background.color']) {
        handleTabChange('style');
      } else if (errors['layout.windowWidth'] || errors['layout.buttonWidth']) {
        handleTabChange('layout');
      } else if (errors['security.secretKey']) {
        handleTabChange('security');
      } else if (errors['responsive.breakpoint']) {
        handleTabChange('responsive');
      }
    }
  );

  const formContent = (
    <form
      id={props.formId ?? 'web-bot-provider-edit-form'}
      onSubmit={handleFormSubmit}
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        flex: 1,
      }}
    >
      <Tabs
        value={activeTab ?? 'text'}
        onChange={handleTabChange}
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          height: '100%',
          flex: 1,
        }}
      >
        <Group justify="space-between" align="center" mb="md" style={{ flexShrink: 0 }}>
          <Tabs.List style={{ flex: 1 }}>
            <Tabs.Tab value="text">文字設定</Tabs.Tab>
            <Tabs.Tab value="style">樣式外觀</Tabs.Tab>
            <Tabs.Tab value="layout">視窗與按鈕</Tabs.Tab>
            <Tabs.Tab value="extension">自訂擴充</Tabs.Tab>
            <Tabs.Tab value="responsive">響應式 RWD</Tabs.Tab>
            <Tabs.Tab value="security">安全認證</Tabs.Tab>
            <Tabs.Tab value="features">其他設定</Tabs.Tab>
          </Tabs.List>
          {props.enableLivePreview !== false && (
            <Tooltip label={showPreview ? '隱藏即時預覽' : '開啟即時預覽'}>
              <ActionIcon variant="light" color="blue" onClick={() => setShowPreview(!showPreview)}>
                {showPreview ? <IconEyeOff size={16} /> : <IconEye size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
        </Group>

        <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 8, paddingBottom: 24 }}>
          <Tabs.Panel value="text">
            <WebBotTextTab form={form} />
          </Tabs.Panel>

          <Tabs.Panel value="style">
            <WebBotStyleTab
              form={form}
              workspaceId={props.workspaceId}
              botProviderId={props.botProviderId}
            />
          </Tabs.Panel>

          <Tabs.Panel value="layout">
            <WebBotLayoutTab
              form={form}
              workspaceId={props.workspaceId}
              botProviderId={props.botProviderId}
            />
          </Tabs.Panel>

          <Tabs.Panel value="extension">
            <WebBotExtensionEdit
              workspaceId={props.workspaceId}
              botProviderId={props.botProviderId}
              form={form}
            />
          </Tabs.Panel>

          <Tabs.Panel value="responsive">
            <WebBotResponsiveTab form={form} />
          </Tabs.Panel>

          <Tabs.Panel value="security">
            <WebBotSecurityTab form={form} />
          </Tabs.Panel>

          <Tabs.Panel value="features">
            <WebBotFeaturesTab form={form} />
          </Tabs.Panel>
        </Box>
      </Tabs>
    </form>
  );

  if (!showPreview) {
    return (
      <Box
        style={{
          height: '100%',
          minHeight: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {formContent}
      </Box>
    );
  }

  return (
    <AppSplitter
      h="100%"
      styles={{
        root: {
          height: '100%',
          minHeight: 0,
          flex: 1,
        },
        pane: {
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        },
      }}
    >
      <AppSplitter.Pane defaultSize={58} min="450px" style={{ paddingRight: 8 }}>
        {formContent}
      </AppSplitter.Pane>
      <AppSplitter.Pane defaultSize={42} min="380px" style={{ paddingLeft: 8 }}>
        <WebBotLivePreview
          data={form.values}
          botProviderId={props.botProviderId}
          workspaceId={props.workspaceId}
        />
      </AppSplitter.Pane>
    </AppSplitter>
  );
}

const tabDefaults: Record<string, Partial<WebBotProviderData>> = {
  text: { text: defaultWebBotProviderData.text },
  style: {
    theme: defaultWebBotProviderData.theme,
    background: defaultWebBotProviderData.background,
    header: defaultWebBotProviderData.header,
    footer: defaultWebBotProviderData.footer,
    userBubble: defaultWebBotProviderData.userBubble,
    botBubble: defaultWebBotProviderData.botBubble,
    messageMeta: defaultWebBotProviderData.messageMeta,
  },
  layout: { layout: defaultWebBotProviderData.layout },
  responsive: { responsive: defaultWebBotProviderData.responsive },
  security: { security: defaultWebBotProviderData.security },
  features: { features: defaultWebBotProviderData.features },
};

export function WebBotProviderDataEditButton(
  props: PropsWithChildren<{
    initValue?: BsonDocument;
    onChange?: (value: BsonDocument) => void;
    workspaceId?: string;
    botProviderId?: string;
  }>
) {
  const [opened, { open, close }] = useDisclosure(false);
  const [data, setData] = useState<BsonDocument>(props.initValue ?? {});
  const [activeTab, setActiveTab] = useState<string | null>('text');

  useEffect(() => {
    if (opened) {
      if (props.initValue) {
        setData(props.initValue);
      }
      setActiveTab('text');
    }
  }, [opened, props.initValue]);

  const handleResetCurrentTab = () => {
    const currentTab = activeTab ?? 'text';
    const defaults = tabDefaults[currentTab];
    if (defaults) {
      setData((prev) => ({
        ...prev,
        ...defaults,
      }));
    }
  };

  const handleSubmit = (values: BsonDocument) => {
    props.onChange?.(values);
    close();
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={close}
        title="Web設定"
        size="96vw"
        styles={{
          body: {
            height: 'calc(95vh - 120px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
          content: {
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Stack style={{ flex: 1, minHeight: 0 }} justify="space-between">
          <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <WebBotProviderDataEditForm
              formId="modal-web-bot-edit-form"
              value={data}
              onChange={setData}
              workspaceId={props.workspaceId}
              botProviderId={props.botProviderId}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              onSubmit={handleSubmit}
            />
          </Box>
          <Group justify="space-between" pt="sm" style={{ flexShrink: 0, borderTop: '1px solid var(--mantine-color-gray-2)' }}>
            <Button variant="default" onClick={handleResetCurrentTab}>
              重設此頁預設值
            </Button>
            <Group>
              <Button variant="default" onClick={close}>
                取消
              </Button>
              <Button
                type="submit"
                form="modal-web-bot-edit-form"
              >
                儲存
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>
      <div onClick={open}>{props.children}</div>
    </>
  );
}
